import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Film, Music, Play, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import {
  AUDIO_ACCEPT, AUDIO_MODES, SHAPES, SHAPE_LABEL, VIDEO_ACCEPT, createRender, fetchRender, fetchRenders, fetchVideoSettings,
  previewUrl, saveVideoSettings, uploadVideoFile, type AudioMode, type Render, type Shape, type VideoSettings,
} from '@/lib/brand-video';
import { qk } from '@/lib/queries';
import { cn } from '@/lib/cn';

const errText = (e: unknown) => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : (e as Error)?.message || 'Something went wrong';
};
const pct = (p: number) => `${Math.round(p * 100)}%`;

/** Brand kit > Video (Aziz 2026-10-29): upload a clip, choose the sound, download it with the
 *  clinic's outro. Managers (Brand details) keep the outros and the music list. */
export default function VideoTab({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const s = useQuery({ queryKey: qk.brandVideo(clientId), queryFn: () => fetchVideoSettings(clientId) });
  if (s.isLoading) return <p className="text-sm text-muted">Loading...</p>;
  if (s.isError || !s.data) return <p className="text-sm text-red-400">Could not load the video settings.</p>;
  const save = async (next: Omit<VideoSettings, 'can_manage'>) => {
    try { qc.setQueryData(qk.brandVideo(clientId), await saveVideoSettings(clientId, next)); toast.success('Saved'); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4">
      <MakeVideo clientId={clientId} settings={s.data} />
      {s.data.can_manage && <ManageCard clientId={clientId} settings={s.data} save={save} />}
    </div>
  );
}

function MakeVideo({ clientId, settings }: { clientId: string; settings: VideoSettings }) {
  const qc = useQueryClient();
  const [clip, setClip] = useState<{ path: string; name: string } | null>(null);
  const [clipProgress, setClipProgress] = useState<number | null>(null);
  const [shape, setShape] = useState<'auto' | Shape>('auto');
  const [audio, setAudio] = useState<AudioMode>('original');
  const [musicId, setMusicId] = useState<string>(settings.music[0]?.id ?? '');
  const [track, setTrack] = useState<{ path: string; name: string } | null>(null);
  const [trackProgress, setTrackProgress] = useState<number | null>(null);
  const [useUpload, setUseUpload] = useState(settings.music.length === 0);
  const [making, setMaking] = useState(false);
  const renders = useQuery({
    queryKey: qk.brandVideoRenders(clientId), queryFn: () => fetchRenders(clientId),
    refetchInterval: (q) => ((q.state.data as Render[] | undefined)?.some((r) => r.status === 'queued' || r.status === 'running') ? 4000 : false),
  });
  const hasOutro = SHAPES.some((x) => settings.outros[x]);
  const needsMusic = audio !== 'original';
  const musicReady = !needsMusic || (useUpload ? !!track : !!musicId);
  const ready = !!clip && hasOutro && (shape === 'auto' || !!settings.outros[shape]) && musicReady && clipProgress === null && trackProgress === null;

  const pickClip = async (f?: File) => {
    if (!f) return;
    if (f.size > 1024 * 1024 * 1024) { toast.error('The clip is over 1 GB. Export a lighter copy first.'); return; }
    setClip(null); setClipProgress(0);
    try { setClip({ path: await uploadVideoFile(clientId, 'clip', f, setClipProgress), name: f.name }); }
    catch (e) { toast.error(errText(e)); }
    finally { setClipProgress(null); }
  };
  const pickTrack = async (f?: File) => {
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast.error('The track is over 50 MB.'); return; }
    setTrack(null); setTrackProgress(0);
    try { setTrack({ path: await uploadVideoFile(clientId, 'track', f, setTrackProgress), name: f.name }); }
    catch (e) { toast.error(errText(e)); }
    finally { setTrackProgress(null); }
  };
  const make = async () => {
    if (!clip) return;
    setMaking(true);
    try {
      await createRender(clientId, {
        clip_path: clip.path, clip_name: clip.name, shape, audio,
        music_id: needsMusic && !useUpload ? musicId : null, track_path: needsMusic && useUpload ? track?.path : null,
      });
      await qc.invalidateQueries({ queryKey: qk.brandVideoRenders(clientId) });
      toast.success('Making the video. It appears below when ready.');
    } catch (e) { toast.error(errText(e)); }
    finally { setMaking(false); }
  };

  return (
    <Card data-testid="video-make">
      <h3 className="text-white font-medium mb-1 flex items-center gap-2"><Film size={16} /> Make a video with the clinic's outro</h3>
      <p className="text-xs text-muted mb-4">Upload a clip, choose the sound, and download it with the outro at the end. Clips up to 5 minutes.</p>
      {!hasOutro && <p className="text-sm text-amber-300 mb-3" data-testid="video-no-outro">This clinic has no outro yet. {settings.can_manage ? 'Add one on the right.' : 'A manager adds it in Brand kit > Video.'}</p>}

      <Step n={1} title="Clip">
        <label className={cn('inline-flex items-center gap-2 text-sm cursor-pointer', clipProgress !== null ? 'text-muted' : 'text-accent-400 hover:underline')}>
          <Upload size={14} /> {clipProgress !== null ? `Uploading ${pct(clipProgress)}` : clip ? 'Replace the clip' : 'Upload a clip'}
          <input type="file" accept={VIDEO_ACCEPT} className="hidden" disabled={clipProgress !== null} data-testid="video-clip-file"
            onChange={(e) => { pickClip(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        {clip && <p className="text-xs text-muted-strong mt-1 truncate" data-testid="video-clip-name">{clip.name}</p>}
      </Step>

      <Step n={2} title="Shape">
        <div className="flex flex-wrap gap-2">
          {(['auto', ...SHAPES] as const).map((x) => {
            const off = x !== 'auto' && !settings.outros[x];
            return (
              <button key={x} type="button" disabled={off} onClick={() => setShape(x)} data-testid={`video-shape-${x}`}
                title={off ? 'No outro in this shape yet' : undefined}
                className={cn('px-3 h-8 rounded-md border text-xs', shape === x ? 'border-accent-400 text-white bg-accent-400/10' : 'border-slate-700 text-muted-strong',
                  off && 'opacity-40 cursor-not-allowed')}>
                {x === 'auto' ? 'Match the clip' : SHAPE_LABEL[x]}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted mt-1">A clip in another shape is placed whole on a soft blurred fill, never stretched or cut.</p>
      </Step>

      <Step n={3} title="Sound">
        <div className="grid sm:grid-cols-2 gap-2">
          {AUDIO_MODES.map((m) => (
            <button key={m.value} type="button" onClick={() => setAudio(m.value)} data-testid={`video-audio-${m.value}`}
              className={cn('text-left rounded-md border px-3 py-2', audio === m.value ? 'border-accent-400 bg-accent-400/10' : 'border-slate-700')}>
              <div className="text-sm text-white">{m.label}</div>
              <div className="text-xs text-muted">{m.hint}</div>
            </button>
          ))}
        </div>
        {needsMusic && (
          <div className="mt-3 space-y-2" data-testid="video-music">
            {settings.music.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-muted-strong">
                <input type="radio" checked={!useUpload} onChange={() => setUseUpload(false)} /> From the clinic's music
                <select className="ml-2 bg-slate-900 border border-slate-700 rounded px-2 h-8 text-sm text-white" value={musicId} disabled={useUpload}
                  onChange={(e) => setMusicId(e.target.value)} data-testid="video-music-pick">
                  {settings.music.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
            )}
            <label className="flex items-center gap-2 text-sm text-muted-strong">
              <input type="radio" checked={useUpload} onChange={() => setUseUpload(true)} /> Upload a track for this video
            </label>
            {useUpload && (
              <label className={cn('ml-6 inline-flex items-center gap-2 text-sm cursor-pointer', trackProgress !== null ? 'text-muted' : 'text-accent-400 hover:underline')}>
                <Music size={14} /> {trackProgress !== null ? `Uploading ${pct(trackProgress)}` : track ? `${track.name} (replace)` : 'Choose a track (MP3, M4A, WAV)'}
                <input type="file" accept={AUDIO_ACCEPT} className="hidden" disabled={trackProgress !== null} data-testid="video-track-file"
                  onChange={(e) => { pickTrack(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
            )}
            <p className="text-xs text-muted">Use only music the clinic owns or has a licence for. The music fades out before the outro.</p>
          </div>
        )}
      </Step>

      <Button icon={<Film size={14} />} loading={making} disabled={!ready} onClick={make} data-testid="video-make-btn">Make the video</Button>

      <h4 className="text-sm text-white mt-6 mb-2">Your videos</h4>
      {(renders.data ?? []).length === 0 ? <p className="text-xs text-muted">None yet. Finished videos stay here for 14 days.</p> : (
        <ul className="divide-y divide-slate-800" data-testid="video-renders">
          {renders.data!.map((r) => <RenderRow key={r.id} clientId={clientId} r={r} />)}
        </ul>
      )}
    </Card>
  );
}

function RenderRow({ clientId, r }: { clientId: string; r: Render }) {
  const [busy, setBusy] = useState(false);
  const mode = AUDIO_MODES.find((m) => m.value === r.audio)?.label ?? r.audio;
  const download = async () => {
    setBusy(true);
    try {
      const full = await fetchRender(clientId, r.id);
      if (!full.download_url) throw new Error('Not ready yet');
      const a = document.createElement('a'); a.href = full.download_url; a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <li className="py-2 flex items-center gap-3" data-testid="video-render-row">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate">{r.clip_name}</div>
        <div className="text-xs text-muted">
          {SHAPE_LABEL[r.shape as Shape] ?? 'Match the clip'} · {mode}{r.music_name ? ` · ${r.music_name}` : ''} · {new Date(r.created_at).toLocaleString()}
        </div>
        {r.status === 'failed' && <div className="text-xs text-red-400">{r.error}</div>}
      </div>
      {r.status === 'done'
        ? <Button size="sm" variant="secondary" icon={<Download size={13} />} loading={busy} onClick={download} data-testid="video-download">Download</Button>
        : <span className={cn('text-xs', r.status === 'failed' ? 'text-red-400' : 'text-amber-300')}>
            {r.status === 'queued' ? 'Waiting' : r.status === 'running' ? 'Making...' : 'Failed'}
          </span>}
    </li>
  );
}

function ManageCard({ clientId, settings, save }: { clientId: string; settings: VideoSettings; save: (s: Omit<VideoSettings, 'can_manage'>) => Promise<void> }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [name, setName] = useState('');
  const plain = { outros: settings.outros, music: settings.music };
  const up = async (slot: string, f: File | undefined, then: (path: string) => Promise<void>) => {
    if (!f) return;
    setBusy(slot); setProgress(0);
    try { await then(await uploadVideoFile(clientId, slot, f, setProgress)); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(null); }
  };
  const play = async (path: string) => {
    try { window.open(await previewUrl(clientId, path), '_blank', 'noopener'); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Card data-testid="video-manage">
      <h3 className="text-white font-medium mb-1">Clinic outros</h3>
      <p className="text-xs text-muted mb-3">The end of every video. One per shape; a clip uses the outro of its shape.</p>
      <ul className="space-y-2 mb-6">
        {SHAPES.map((x) => {
          const path = settings.outros[x];
          return (
            <li key={x} className="flex items-center gap-2 text-sm" data-testid={`video-outro-${x}`}>
              <span className="w-28 text-muted-strong">{SHAPE_LABEL[x]}</span>
              {path ? <button className="text-accent-400 hover:underline inline-flex items-center gap-1" onClick={() => play(path)}><Play size={12} /> Play</button>
                : <span className="text-xs text-slate-500">none yet</span>}
              <label className="ml-auto text-xs text-accent-400 cursor-pointer hover:underline inline-flex items-center gap-1">
                <Upload size={12} /> {busy === `outro_${x}` ? `Uploading ${pct(progress)}` : path ? 'Replace' : 'Upload'}
                <input type="file" accept={VIDEO_ACCEPT} className="hidden" disabled={!!busy}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; up(`outro_${x}`, f, (p) => save({ ...plain, outros: { ...plain.outros, [x]: p } })); }} />
              </label>
              {path && <button className="text-xs text-muted hover:text-red-400" onClick={() => save({ ...plain, outros: { ...plain.outros, [x]: '' } })}>Remove</button>}
            </li>
          );
        })}
      </ul>

      <h3 className="text-white font-medium mb-1">Music list</h3>
      <p className="text-xs text-muted mb-3">Tracks the clinic owns or has a licence for. Everyone making a video picks from these.</p>
      <ul className="space-y-1 mb-3" data-testid="video-music-list">
        {settings.music.map((t) => (
          <li key={t.id} className="flex items-center gap-2 text-sm">
            <Music size={13} className="text-muted" /><span className="text-white truncate flex-1">{t.name}</span>
            <button className="text-accent-400 hover:underline text-xs" onClick={() => play(t.path)}>Play</button>
            <button className="text-muted hover:text-red-400" aria-label={`Remove ${t.name}`} onClick={() => save({ ...plain, music: plain.music.filter((m) => m.id !== t.id) })}><Trash2 size={13} /></button>
          </li>
        ))}
        {settings.music.length === 0 && <li className="text-xs text-slate-500">No tracks yet.</li>}
      </ul>
      <div className="flex items-center gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Track name" maxLength={120} data-testid="video-music-name"
          className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 h-8 text-sm text-white" />
        <label className={cn('text-xs inline-flex items-center gap-1', name.trim() && !busy ? 'text-accent-400 cursor-pointer hover:underline' : 'text-slate-500 cursor-not-allowed')}>
          <Upload size={12} /> {busy === 'music' ? `Uploading ${pct(progress)}` : 'Add a track'}
          <input type="file" accept={AUDIO_ACCEPT} className="hidden" disabled={!name.trim() || !!busy} data-testid="video-music-file"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = '';
              up('music', f, async (p) => { await save({ ...plain, music: [...plain.music, { id: '', name: name.trim(), path: p }] }); setName(''); }); }} />
        </label>
      </div>
    </Card>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div className="text-xs uppercase tracking-wide text-muted mb-2"><span className="text-accent-400 mr-1">{n}</span>{title}</div>
      {children}
    </div>
  );
}
